import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';

import { useSettings } from 'hooks/useSettings';

import { storageKeys } from 'config/settings.json';

const userInfoContext = createContext();

const defaultUserInfo = {
  loaded: false,
  user: {
    _id: '',
    email: '',
    type_of_user: '',
    agency_name: '',
    first_name: '',
    last_name: '',
    address: '',
    address2: '',
    address3: '',
    city: '',
    state: '',
    country: '',
    title: '',
    zip_code: '',
    phone: '',
    mobile_phone: '',
    mobile_phone_number: '',
    id_type: '',
    id_number: '',
    sf_id: null,
    agency_business: '',
    agency_website: '',
    agency_revenue: '',
    agency_affiliation: '',
    host_agency: null,
    host_agency_name: null,
    owner: null,
    years_of_experience: '',
    annual_sales: '',
    destination_1: '',
    destination_2: '',
    destination_3: '',
    travel_type_1: '',
    travel_type_2: '',
    travel_type_3: '',
    travel_type_4: '',
    sales_companion_registered: '',
    training_started: '',
    training_graduated: null,
    device: null,
    registration_code: null,
    fast_track: null,
    fast_track_date: null,
    fast_track_score: null,
    fast_track_duration: 0,
    custom_1: null,
    custom_2: null,
    custom_3: null,
    custom_4: null,
    custom_5: null,
    custom_6: null,
    custom_7: null,
    custom_8: null,
    custom_9: null,
    custom_10: null,
    marketing_opt_in: null,
    marketing_travel_brands: null,
    owner_manager: null,
    profile_image: null,
    profile_image_url: null,
  },
};

/**
 * Provides a context for user information, including user details, progress, and associated loading and error states.
 * This provider is meant to encapsulate and provide user-related data and functions to its child components.
 *
 * @param {object} props - The component props.
 * @param {React.ReactNode} props.children - The children components that can consume the context.
 * @returns {React.ReactNode} The provider component.
 */
const UserInfoProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(defaultUserInfo);

  // requires 1 or more updates to updates. Either an array or a single object works.
  const updateUser = useCallback(
    async (appId, params = []) => {
      const {
        user: { email },
      } = userInfo;
      const paramArray = Array.isArray(params) ? params : [params];
      if (!email || !appId || paramArray.length === 0) {
        return false;
      }

      setUserInfo((prev) => ({
        ...prev,
        loaded: false,
      }));

      const updateResponse = await APIHandler.updateUser(appId, email, paramArray);

      if (!updateResponse) return false;

      const { user: newUser } = userInfo;

      paramArray.forEach(({ param, val }) => {
        newUser[param] = val;
      });

      StorageHandler.save(storageKeys.user, newUser);
      setUserInfo({
        loaded: true,
        user: newUser,
      });

      return true;
    },
    [userInfo],
  );

  /**
   * Fetches user information from storage
   *
   * @param {number} appId - The application ID.
   */
  const fetchUser = useCallback(async (appId) => {
    /**
     * If the token does not exist, the user is not logged in. Do not attempt to fetch data, but also do not attempt to
     * redirect. Pages that require a user to be logged in should be handled by the router + login check component, and removing this
     * check would break the ability to visit sites that may attempt to load user specific data but do not require a user to be logged in (e.g. home).
     * These pages should have their own checks and balances to ensure that the user is logged in before attempting to load user data.
     */
    const tokenExists = await StorageHandler.exists(storageKeys.accessToken);
    if (!tokenExists) return;

    const tokenExpired = await StorageHandler.isExpired(storageKeys.accessToken);

    // Redirect the user if the token exists, but is expired. This will log the user out. No need to fetch.
    if (tokenExpired) window.location.href = '/logout';

    // Fetch the user data
    const accessToken = await StorageHandler.load(storageKeys.accessToken);
    const userResponse = await APIHandler.getUser(appId, accessToken);

    // Redirect if the user is not authorized. This will log the user out.
    if (userResponse?.response?.status === 401) window.location.href = '/logout';

    if (userResponse?.email) {
      StorageHandler.save(storageKeys.user, userResponse);
      setUserInfo({
        loaded: true,
        user: userResponse,
      });
    }
  }, []);

  // Memoizes the context values to prevent unnecessary re-renders
  const userInfoValues = useMemo(() => {
    const { user, loaded } = userInfo;

    return {
      loaded,
      user,
      fetchUser,
      updateUser,
    };
  }, [fetchUser, updateUser, userInfo]);

  return <userInfoContext.Provider value={userInfoValues}>{children}</userInfoContext.Provider>;
};

UserInfoProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * @typedef {object} UserInfo
 * @property {boolean} loaded - The hook has finished loading the data.
 * @property {object | null} user - The user data or `null` if not loaded or in case of an error.
 * @property {object | null} progress - The progress data or `null` if not loaded or in case of an error.
 * @property {function(): void} refresh - Re-fetches the user data from the API.
 * @property {function(number, Array<{param: string, val: *}>): Promise<boolean>} updateUser - Updates user fields on the API and in local storage.
 */

/**
 * Custom React hook for accessing user information within the `UserInfoProvider` context. This hook must be used within
 * a `UserInfoProvider` component.
 *
 * @returns {UserInfo} An object containing user information, progress details, loading and error states.
 */
const useUserInfo = () => {
  const context = useContext(userInfoContext);

  if (!context) {
    throw new Error('useUserInfo must be used within a UserInfoProvider');
  }

  const { loaded, user, updateUser, fetchUser } = context;

  const { appId, loaded: appSettingsLoaded } = useSettings();

  useEffect(() => {
    if (appSettingsLoaded && appId) {
      fetchUser(appId);
    }
  }, [appId, appSettingsLoaded, fetchUser]);

  const refresh = useCallback(() => {
    if (appSettingsLoaded && appId) {
      fetchUser(appId);
    }
  }, [appSettingsLoaded, fetchUser, appId]);

  return {
    loaded,
    user,
    updateUser,
    refresh,
  };
};

export { UserInfoProvider, useUserInfo };
